import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  PlayArrow,
  Schedule,
  Equalizer,
} from '@mui/icons-material';
import {
  useAnalyticsStats,
  useSystemHealth,
} from '../hooks/useAnalytics';

const Analytics: React.FC = () => {
  const { data: analyticsStats, isLoading: statsLoading, error: statsError } = useAnalyticsStats();
  const { data: systemHealth, isLoading: healthLoading } = useSystemHealth();

  // Loading state
  if (statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (statsError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load analytics data: {(statsError as any)?.message || 'Unknown error'}
      </Alert>
    );
  }

  const stats = [
    {
      title: 'Total Plays',
      value: analyticsStats?.total_plays?.toLocaleString() || '0',
      icon: <PlayArrow sx={{ color: '#4a9eff' }} />,
      color: '#4a9eff',
    },
    {
      title: 'Active Sessions',
      value: analyticsStats?.active_sessions?.toLocaleString() || '0',
      icon: <Schedule sx={{ color: '#10b981' }} />,
      color: '#10b981',
    },
    {
      title: 'Total Content',
      value: analyticsStats?.total_content?.toLocaleString() || '0',
      icon: <TrendingUp sx={{ color: '#ff6b35' }} />,
      color: '#ff6b35',
    },
    {
      title: 'Processing Queue',
      value: analyticsStats?.processing_queue?.toString() || '0',
      icon: <Equalizer sx={{ color: '#8b5cf6' }} />,
      color: '#8b5cf6',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Analytics
      </Typography>

      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: `${stat.color}20`,
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Top Performing Content
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Content Name</TableCell>
                    <TableCell>Plays</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Performance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No content data yet. Upload and play content to see performance metrics.
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Usage Patterns
            </Typography>
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Usage data will appear here once content is being played.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Recent Activity
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                No recent activity. Actions like uploads, approvals, and playlist changes will appear here.
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              System Health
            </Typography>
            {healthLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">API Status</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: systemHealth?.api_status === 'healthy' ? '#10b981' :
                               systemHealth?.api_status === 'degraded' ? '#f59e0b' : '#ef4444'
                      }}
                    >
                      {systemHealth?.api_status === 'healthy' ? 'Healthy' :
                       systemHealth?.api_status === 'degraded' ? 'Degraded' :
                       systemHealth?.api_status === 'down' ? 'Down' : 'Unknown'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={systemHealth?.api_status === 'healthy' ? 100 :
                           systemHealth?.api_status === 'degraded' ? 50 : 0}
                    sx={{
                      '& .MuiLinearProgress-bar': {
                        bgcolor: systemHealth?.api_status === 'healthy' ? '#10b981' :
                                 systemHealth?.api_status === 'degraded' ? '#f59e0b' : '#ef4444'
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Database Status</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: systemHealth?.database_status === 'healthy' ? '#10b981' : '#ef4444'
                      }}
                    >
                      {systemHealth?.database_status === 'healthy' ? 'Connected' : 'Disconnected'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={systemHealth?.database_status === 'healthy' ? 100 : 0}
                    sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#4a9eff' } }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Storage Status</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: systemHealth?.storage_status === 'healthy' ? '#10b981' : '#ef4444'
                      }}
                    >
                      {systemHealth?.storage_status === 'healthy' ? 'Available' : 'Unavailable'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={systemHealth?.storage_status === 'healthy' ? 100 : 0}
                    sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#ff6b35' } }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Response Time</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {systemHealth?.response_time || 0}ms
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((systemHealth?.response_time || 0) / 5, 100)}
                    sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6' } }}
                  />
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
